import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  ChangeDataSourceAccess,
  ChangeDataSourceAccessVariables
} from "./schema/ChangeDataSourceAccess";
import {
  QueryDataSource,
  QueryDataSourceVariables
} from "./schema/QueryDataSource";
import { QueryDataSourceApiKeys } from "./schema/QueryDataSourceApiKeys";
import {
  QueryDataSourceOpenApiSpec,
  QueryDataSourceOpenApiSpecVariables
} from "./schema/QueryDataSourceOpenApiSpec";
import {
  QueryDataSources,
  QueryDataSourcesVariables
} from "./schema/QueryDataSources";
import {
  QueryDataSourceVersion,
  QueryDataSourceVersionVariables
} from "./schema/QueryDataSourceVersion";
import {
  RepublishDataSourceVersion,
  RepublishDataSourceVersionVariables
} from "./schema/RepublishDataSourceVersion";
import {
  UnpublishDataSourceVersion,
  UnpublishDataSourceVersionVariables
} from "./schema/UnpublishDataSourceVersion";

export const dataSourcesQuery = gql`
  query QueryDataSources($solutionKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        cloudDataSources {
          dataSources {
            key
            creationDate
            creator {
              name
              firstName
              lastName
            }
            access
            allVersions {
              version
            }
          }
        }
      }
    }
  }
`;

export const dataSourceQuery = gql`
  query QueryDataSource($solutionKey: Key!, $dataSourceKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            key
            creationDate
            creator {
              name
              firstName
              lastName
            }
            access
            allVersions(includeUnpublished: true) {
              version
              isLive
              comment
              creationDate
              creator {
                name
                firstName
                lastName
              }
              modificationDate
              modifier {
                name
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`;

export const dataSourceVersionQuery = gql`
  query QueryDataSourceVersion(
    $solutionKey: Key!
    $dataSourceKey: Key!
    $version: String!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            key
            version(version: $version, includeUnpublished: true) {
              version
              schema {
                name
                displayName
                columns {
                  name
                  displayName
                  typeName
                  nullable
                }
              }
              openApiSpec(format: Json)
            }
          }
        }
      }
    }
  }
`;

export const dataSourceOpenApiSpecQuery = gql`
  query QueryDataSourceOpenApiSpec(
    $solutionKey: Key!
    $dataSourceKey: Key!
    $version: String!
    $format: OpenApiFormat!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            key
            version(version: $version, includeUnpublished: true) {
              version
              openApiSpec(format: $format)
            }
          }
        }
      }
    }
  }
`;

export const dataSourceApiKeysQuery = gql`
  query QueryDataSourceApiKeys {
    publicApi {
      apiKeys {
        apiKeys {
          id
          name
        }
      }
    }
  }
`;

export const changeDataSourceAccessMutation = gql`
  mutation ChangeDataSourceAccess(
    $solutionKey: Key!
    $dataSourceKey: Key!
    $apiKeyId: String!
    $access: Boolean!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            changeAccess(apiKeyId: $apiKeyId, access: $access)
          }
        }
      }
    }
  }
`;

export const unpublishDataSourceVersionMutation = gql`
  mutation UnpublishDataSourceVersion(
    $solutionKey: Key!
    $dataSourceKey: Key!
    $version: String!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            unpublishVersion(version: $version) {
              version
              isLive
            }
          }
        }
      }
    }
  }
`;

export const republishDataSourceVersionMutation = gql`
  mutation RepublishDataSourceVersion(
    $solutionKey: Key!
    $dataSourceKey: Key!
    $version: String!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        cloudDataSources {
          dataSource(key: $dataSourceKey) {
            republishVersion(version: $version) {
              version
              isLive
            }
          }
        }
      }
    }
  }
`;

export const useDataSources = (solutionKey: string) =>
  useQuery<QueryDataSources, QueryDataSourcesVariables>(dataSourcesQuery, {
    fetchPolicy: "no-cache",
    variables: { solutionKey }
  });

export const useDataSource = (solutionKey: string, dataSourceKey: string) =>
  useQuery<QueryDataSource, QueryDataSourceVariables>(dataSourceQuery, {
    fetchPolicy: "no-cache",
    variables: { solutionKey, dataSourceKey }
  });

export const useDataSourceVersion = (
  solutionKey: string,
  dataSourceKey: string,
  version: string
) =>
  useQuery<QueryDataSourceVersion, QueryDataSourceVersionVariables>(
    dataSourceVersionQuery,
    {
      fetchPolicy: "no-cache",
      variables: { solutionKey, dataSourceKey, version },
      skip: !version
    }
  );

export const useDataSourceApiKeys = () =>
  useQuery<QueryDataSourceApiKeys>(dataSourceApiKeysQuery);

export const useDataSourceOpenApiSpec = () =>
  useLazyQuery<QueryDataSourceOpenApiSpec, QueryDataSourceOpenApiSpecVariables>(
    dataSourceOpenApiSpecQuery,
    { fetchPolicy: "no-cache" }
  );

export const useChangeDataSourceAccess = () =>
  useMutation<ChangeDataSourceAccess, ChangeDataSourceAccessVariables>(
    changeDataSourceAccessMutation
  );

export const useUnpublishDataSourceVersion = () =>
  useMutation<UnpublishDataSourceVersion, UnpublishDataSourceVersionVariables>(
    unpublishDataSourceVersionMutation
  );

export const useRepublishDataSourceVersion = () =>
  useMutation<RepublishDataSourceVersion, RepublishDataSourceVersionVariables>(
    republishDataSourceVersionMutation
  );
